import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PlaceIcon from "@mui/icons-material/Place";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrCopyIcon from "../../../../components/BdrCopyIcon/BdrCopyIcon";
import {
  GET_AVAILABLE_SOONER_EVENTS,
  GET_MY_TASK_NOTE,
} from "../../../../utils/GraphQL/queries";
import { useSSEListener } from "../../../../utils/SSE/SSEContext";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { formatCustomerAddress } from "../../../../utils/formatters/customerAddress";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import TaskNoteModal from "./TaskNoteModal";
import DriveTimeButton from "../DriveTime/DriveTimeButton";
import {
  makePlace,
  useDriveTimePins,
} from "../../../../utils/DriveTimePin/DriveTimePinContext";
import {
  formatMinutes,
  hasAddress,
  toAddressInput,
} from "../../../../utils/DriveTimePin/driveTimeFormat";
import { GET_DRIVE_LEGS } from "../../../../utils/GraphQL/queries";
import { useTourAction } from "../../../../tours/tourActions";
import { useTourModal } from "../../../../tours/tourModals";
import { DEMO_CUSTOMER, DEMO_ROW_DRIVE_TEXT } from "../DriveTime/driveTimeDemo";
import "./availSoonerNotePanel.css";

/**
 * The Tasks console's right-hand panel: Avail. Sooner | User Note.
 *
 * ─────────────────────────────────────────────────────────────────
 * REPLACED THE "TODAY" STRIP, which had two live bugs: every row read
 * "(no label)" (label is personal-only by design, so a job event never has
 * one), and the "+ N more" toggle painted on top of the third row.
 *
 * TWO MOUNT SITES, ONE LIVE AT A TIME. `AdminDashboardTasks` renders this in
 * a `useTaskModal &&` branch and a `useTaskModal === false &&` branch, and
 * `useTaskModal` is the sub-900px media query — so they are mutually
 * exclusive and only one instance ever mounts.
 *
 * (An earlier draft of this comment claimed both mounted and that Apollo
 * deduplicated them. That was simply wrong. The queries still take no
 * arguments, but the reason is that the ASAP window is SERVER-owned and the
 * note resolves off `context.user` — not deduplication.)
 *
 * `network-only` on both. A cached ASAP row is worse than a slow one — the
 * list exists to answer "who can I move into this slot right now", and a stale
 * entry sends someone to call a customer whose slot was already filled.
 *
 * Plus a live SSE refetch: the fifth consumer of `calendar_update`, after
 * DayModal, DayViewModal, WeekModal and CalendarPage.
 */

const AvailSoonerNotePanel = ({ canSeeAvailSooner = false }) => {
  const navigate = useNavigate();
  const { handleSnack } = useSnackbar();

  const [events, setEvents] = useState([]);
  const [note, setNote] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [getAvailSooner] = useLazyQuery(GET_AVAILABLE_SOONER_EVENTS, {
    fetchPolicy: "network-only",
  });
  const [getNote] = useLazyQuery(GET_MY_TASK_NOTE, {
    fetchPolicy: "network-only",
  });

  /* Silent: no loading flag, so an SSE-driven refresh updates the list in
     place rather than flashing a spinner while someone is reading it. Same
     shape as DayModal's silentRefetchDayData. */
  /* Returns whether it actually fetched, so the caller can tell a real empty
     list apart from a pass that never ran. */
  const loadEvents = useCallback(async () => {
    if (!canSeeAvailSooner) return false;
    try {
      const { data } = await getAvailSooner();
      setEvents(data?.getAvailableSoonerEvents || []);
    } catch (err) {
      console.error("Avail. Sooner load failed:", err?.message || err);
    }
    return true;
  }, [canSeeAvailSooner, getAvailSooner]);

  const loadNote = useCallback(async () => {
    try {
      const { data } = await getNote();
      setNote(data?.getMyTaskNote ?? null);
    } catch (err) {
      console.error("Task note load failed:", err?.message || err);
    }
  }, [getNote]);

  /*
   * `loaded` must mean "the list fetch actually ran", not "an effect fired".
   *
   * `perm` starts empty in the host, so the first pass has
   * `canSeeAvailSooner === false` and `loadEvents` early-returns. Setting
   * `loaded` there made the column render "Nobody is waiting for an earlier
   * slot." the instant the role arrived — a false empty state, which is
   * exactly what the skeleton exists to prevent.
   */
  useEffect(() => {
    (async () => {
      const [ran] = await Promise.all([loadEvents(), loadNote()]);
      if (ran) setLoaded(true);
    })();
  }, [loadEvents, loadNote]);

  /* Any calendar change can add, remove or move an ASAP event, so this
     refetches on every `calendar_update` rather than inspecting the payload —
     the query is <=50 narrow rows and the events are human-paced. */
  useSSEListener("calendar_update", loadEvents);

  /*
   * Drive time per row (registry §17.10). While something is pinned, every
   * row shows its legs from the start pin and to the end pin — ONE matrix call
   * for the whole list (1×N and N×1), re-run only when the pins or the set of
   * customers changes. A cancellation is exactly when this list is read, and
   * "who on it is near the gap" is the question.
   */
  const pins = useDriveTimePins();
  const [getLegs] = useLazyQuery(GET_DRIVE_LEGS, { fetchPolicy: "network-only" });
  const [legsByCustomer, setLegsByCustomer] = useState({});
  const [legsState, setLegsState] = useState("idle"); // idle | loading | ready | budget | error
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const pinKey = JSON.stringify([pins.start?.address || null, pins.end?.address || null]);
  const stopsKey = events
    .map((r) => r?.customer?._id)
    .filter(Boolean)
    .join(",");

  useEffect(() => {
    if (!pins.hasAny || !stopsKey) {
      setLegsByCustomer({});
      setLegsState("idle");
      return undefined;
    }
    const rows = (eventsRef.current || []).filter((r) => hasAddress(r?.customer));
    if (rows.length === 0) {
      setLegsByCustomer({});
      setLegsState("idle");
      return undefined;
    }
    const [startAddr, endAddr] = JSON.parse(pinKey);
    let alive = true;
    setLegsState("loading");
    getLegs({
      variables: {
        start: startAddr,
        end: endAddr,
        stops: rows.map((r) => toAddressInput(r.customer)),
      },
    })
      .then(({ data }) => {
        if (!alive) return;
        const res = data?.getDriveLegs;
        const map = {};
        rows.forEach((r, i) => {
          map[r.customer._id] = res?.stops?.[i] || null;
        });
        setLegsByCustomer(map);
        setLegsState(res?.budgetExhausted ? "budget" : "ready");
      })
      .catch((err) => {
        if (!alive) return;
        console.error("Drive legs load failed:", err?.message || err);
        setLegsState("error");
      });
    return () => {
      alive = false;
    };
  }, [pins.hasAny, pinKey, stopsKey, getLegs]);

  /*
   * "web:drive-time-pins" walkthrough demo row (registry §17.10, guide §5):
   * ONE extra row, first in the list, with the real row's markup and anchors,
   * a fixed example customer and static drive text. It never touches
   * `events`; registered as the tagged surface "avail-sooner-demo" so the
   * engine clears it like a sheet when the tour moves on or ends.
   */
  const [demoRow, setDemoRow] = useState(false);
  useTourAction("avail-sooner-demo", () => setDemoRow(true));
  useTourModal("avail-sooner-demo", demoRow, () => setDemoRow(false));

  /* "12 min from start · 18 min to end", or what went wrong, per row. */
  const driveText = (customer) => {
    if (!pins.hasAny) return null;
    if (!hasAddress(customer)) return "no address";
    if (legsState === "loading" || legsState === "idle") return "…";
    if (legsState === "error") return "drive time unavailable";
    if (legsState === "budget") return "daily lookups used up";
    const legs = legsByCustomer[customer?._id];
    if (!legs) return "—";
    const parts = [];
    if (pins.start) {
      parts.push(
        legs.fromStart?.ok
          ? `${formatMinutes(legs.fromStart.durationSec)} from start`
          : "no route from start"
      );
    }
    if (pins.end) {
      parts.push(
        legs.toEnd?.ok ? `${formatMinutes(legs.toEnd.durationSec)} to end` : "no route to end"
      );
    }
    return parts.join(" · ");
  };

  /* Which row just copied — its pin icon shows the check for a moment (the
     same affordance as the client rail: pin at rest, copy glyph on hover). */
  const [copiedId, setCopiedId] = useState(null);
  const copiedTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  const handleCopyAddress = async (customer) => {
    const address = formatCustomerAddress(customer);
    if (!address) return;

    try {
      await navigator.clipboard.writeText(address);
      setCopiedId(customer?._id || null);
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopiedId(null), 1200);
      handleSnack("Address copied", 2000);
    } catch (err) {
      handleSnack("Could not copy the address", 3000);
    }
  };

  /*
   * Hands the event to the CALENDAR rather than opening a popup here.
   *
   * `EventDetailPopup`'s Edit button is unguarded, and every existing host
   * also renders EditEventModal plus the invoice and quote modals — so showing
   * it on this page would mean hosting three more modals on the post-login
   * landing page, and fetching the invoices/quotes/phones/notes they need into
   * a payload that is deliberately narrow. WeekModal already hosts all of it.
   */
  const handleOpenOnCalendar = (row) => {
    // A window CustomEvent, not navigate(): the calendar is a modal owned by
    // AdminNavbar, which is a SIBLING of <Routes> — there is no route to go to
    // and no shared state to set. Same shape as `bdr:clockin-changed`.
    window.dispatchEvent(
      new CustomEvent("bdr:open-calendar-event", {
        detail: {
          dayMs: row?.calendarItem?.startMs,
          eventId: row?.calendarItem?.id,
        },
      })
    );
  };

  const clientName = (customer) =>
    `${customer?.firstName || ""} ${customer?.lastName || ""}`.trim() ||
    "(no name)";

  return (
    <div className="asap-panel bdr-section-panel">
      {/* Two 50% boxes in the header band, each centred over its own column. */}
      <div className="bdr-section-panel-hdr asap-panel-hdr">
        {canSeeAvailSooner && (
          <h3 className="asap-panel-hdr-box">Avail. Sooner</h3>
        )}
        <h3 className="asap-panel-hdr-box">User Note</h3>
      </div>

      <div className="bdr-section-panel-body asap-panel-body">
        {/* data-tour anchors: "web:avail-sooner" walkthrough (tours registry). */}
        {canSeeAvailSooner && (
          <div className="asap-col asap-col--list" data-tour="avail-sooner-list">
            {!loaded ? (
              <div className="asap-skeleton" aria-hidden />
            ) : events.length === 0 && !demoRow ? (
              <div className="bdr-empty">Nobody is waiting for an earlier slot.</div>
            ) : (
              <ul className="asap-list">
                {demoRow && (
                  <li className="asap-row" data-tour="avail-sooner-row">
                    <div className="asap-row-main">
                      <BdrButton size="sm" variant="plain" className="asap-row-name" disabled>
                        Example Customer
                      </BdrButton>
                      <span className="asap-row-date">
                        {BAGroupTimeFormat(String(Date.now()), false, false)}
                      </span>
                      <span className="asap-row-drive" title="Example">
                        {DEMO_ROW_DRIVE_TEXT}
                      </span>
                    </div>
                    <div className="asap-row-actions">
                      <span data-tour="avail-sooner-drive">
                        <DriveTimeButton
                          size="sm"
                          variant="action"
                          place={makePlace("Example Customer", DEMO_CUSTOMER)}
                        />
                      </span>
                      <BdrButton
                        size="sm"
                        variant="action"
                        iconOnly
                        className="bdr-copy-host"
                        icon={<BdrCopyIcon icon={<PlaceIcon />} />}
                        ariaLabel="Copy address"
                        title="Copy address"
                        disabled
                      />
                      <BdrButton
                        size="sm"
                        variant="action"
                        iconOnly
                        icon={<CalendarMonthIcon />}
                        title="Show on the calendar"
                        disabled
                      />
                    </div>
                  </li>
                )}
                {events.map((row) => (
                  <li
                    key={row?.calendarItem?.id}
                    className="asap-row"
                    data-tour="avail-sooner-row"
                  >
                    <div className="asap-row-main">
                      <BdrButton
                        size="sm"
                        variant="plain"
                        className="asap-row-name"
                        title="Open customer"
                        onClick={() =>
                          navigate(
                            `/bdr-office/clients/view-client/${row?.customer?._id}`
                          )
                        }
                        disabled={!row?.customer?._id}
                      >
                        {clientName(row?.customer)}
                      </BdrButton>
                      <span className="asap-row-date">
                        {BAGroupTimeFormat(row?.calendarItem?.startMs, false, false)}
                      </span>
                      {pins.hasAny && (
                        <span className="asap-row-drive" title="Typical drive time without traffic">
                          {driveText(row?.customer)}
                        </span>
                      )}
                    </div>

                    <div className="asap-row-actions">
                      <span data-tour="avail-sooner-drive">
                        <DriveTimeButton
                          size="sm"
                          variant="action"
                          place={makePlace(clientName(row?.customer), row?.customer)}
                        />
                      </span>
                      {navigator.clipboard && (
                        <BdrButton
                          size="sm"
                          variant="action"
                          iconOnly
                          className="bdr-copy-host"
                          icon={
                            <BdrCopyIcon
                              icon={<PlaceIcon />}
                              copied={copiedId === row?.customer?._id}
                            />
                          }
                          ariaLabel="Copy address"
                          title="Copy address"
                          onClick={() => handleCopyAddress(row?.customer)}
                          disabled={!formatCustomerAddress(row?.customer)}
                        />
                      )}
                      <BdrButton
                        size="sm"
                        variant="action"
                        iconOnly
                        icon={<CalendarMonthIcon />}
                        title="Show on the calendar"
                        onClick={() => handleOpenOnCalendar(row)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/*
          The COLUMN ITSELF is the button — one element, not a button nested
          in a div.

          It was a bordered box inside a padded column, so the chrome was
          doubled: the column's padding, then the note's own border and
          background inside it. In a panel this small that is a visible chunk
          of the only space there is. One element means the whole box is the
          hit target and the whole box lights up.

          A <button>, not a div with onClick, so it is keyboard-reachable and
          announces itself as something that can be activated.

          `data-tour` is the "web:task-note" walkthrough's anchor (tours
          registry): rename or remove it and that tour's test fails.
        */}
        <button
          type="button"
          data-tour="task-note"
          className="asap-col asap-col--note"
          onClick={() => setNoteOpen(true)}
          title="Edit your note"
        >
          {note ? (
            <span className="asap-note-text">{note}</span>
          ) : (
            <span className="asap-note-empty">
              Click to add a note. Only you can see it.
            </span>
          )}
        </button>
      </div>

      <TaskNoteModal
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        note={note}
        onSaved={setNote}
      />
    </div>
  );
};

export default AvailSoonerNotePanel;
